// src/i18n/LanguageProvider.tsx

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';

import { getBackgroundImagesForEvent } from '@/constants/images';
import {
  createI18nAsync,
} from './locales/i18n.async';
import {
  type AppLang,
  type I18nConstants,
} from './locales/i18n.factory';

type ContextType = {
  lang: AppLang;
  setLang: (l: AppLang) => void;
  toggleLang: () => void;   // ⭐️ חדש
  i18n: I18nConstants;
};

const I18nContext = createContext<ContextType | null>(null);
const LANG_STORAGE_KEY = 'djeliormor.lang';
const SUPPORTED_LANGS: AppLang[] = ['he', 'en', 'ru', 'fr'];
const loadedI18n: Partial<Record<AppLang, I18nConstants>> = {};
const initialLoaderImages = getBackgroundImagesForEvent('אירוע').dark;
const LANGUAGE_LOADER_MOBILE_IMAGE = initialLoaderImages.mobile[0];
const LANGUAGE_LOADER_DESKTOP_IMAGE = initialLoaderImages.desktop[0];

const LANGUAGE_LOADING_COPY: Record<AppLang, { brand: string; subtitle: string; label: string }> = {
  he: {
    brand: 'DJ אליאור מור',
    subtitle: 'טוען את האתר בעברית...',
    label: 'טוען עברית',
  },
  en: {
    brand: 'DJ Elior Mor',
    subtitle: 'Loading the site in English...',
    label: 'Loading English',
  },
  ru: {
    brand: 'DJ Elior Mor',
    subtitle: 'Загружаем сайт на русском...',
    label: 'Loading Russian',
  },
  fr: {
    brand: 'DJ Elior Mor',
    subtitle: 'Chargement du site en français...',
    label: 'Loading French',
  },
};

const isInitialLoaderVisible = () => {
  if (typeof document === 'undefined') return false;
  return (
    !!document.getElementById('human-loader') &&
    !document.documentElement.classList.contains('spa-ready')
  );
};

const LoaderOverlay = styled.div<{ $dir: 'rtl' | 'ltr' }>`
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: hidden;
  background: #050505;
  color: #fff;
  direction: ${({ $dir }) => $dir};
  font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;

  &::before {
    content: '';
    position: absolute;
    inset: -18px;
    background-image: url(${LANGUAGE_LOADER_MOBILE_IMAGE});
    background-size: cover;
    background-position: center;
    filter: saturate(1.08) contrast(1.04);
    transform: scale(1.02);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(180deg, rgba(0, 0, 0, 0.78), rgba(0, 0, 0, 0.42) 44%, rgba(0, 0, 0, 0.86)),
      radial-gradient(circle at 50% 42%, rgba(216, 166, 64, 0.22), rgba(0, 0, 0, 0) 44%),
      radial-gradient(circle at 50% 120%, rgba(0, 0, 0, 0.82), rgba(0, 0, 0, 0) 34%);
  }

  @media (min-width: 768px) {
    &::before {
      background-image: url(${LANGUAGE_LOADER_DESKTOP_IMAGE});
    }
  }
`;

const LoaderCard = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  gap: 10px;
  width: min(620px, 90vw);
  border-radius: 24px;
  padding: 24px 22px;
  background: linear-gradient(180deg, rgba(12, 12, 12, 0.58), rgba(12, 12, 12, 0.38));
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.42);
  text-align: center;
  backdrop-filter: blur(12px);
`;

const LoaderKicker = styled.div`
  color: #d8a640;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
`;

const LoaderBars = styled.div`
  display: flex;
  align-items: end;
  justify-content: center;
  gap: 5px;
  height: 22px;
  margin-top: 2px;

  span {
    display: block;
    width: 5px;
    height: 10px;
    border-radius: 999px;
    background: #d8a640;
    box-shadow: 0 0 18px rgba(216, 166, 64, 0.55);
    animation: language-loader-bars 0.78s ease-in-out infinite;
  }

  span:nth-child(2) {
    animation-delay: 0.1s;
  }

  span:nth-child(3) {
    animation-delay: 0.2s;
  }

  span:nth-child(4) {
    animation-delay: 0.3s;
  }

  @keyframes language-loader-bars {
    0%,
    100% {
      height: 8px;
      opacity: 0.62;
    }

    50% {
      height: 22px;
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    span {
      animation: none;
      height: 14px;
    }
  }
`;

const LoaderTitle = styled.div`
  font-weight: 800;
  letter-spacing: 0;
  font-size: clamp(27px, 7.4vw, 54px);
  line-height: 1.1;
  text-shadow: 0 3px 18px rgba(0, 0, 0, 0.72);
`;

const LoaderSubtitle = styled.div`
  color: #f4e5bd;
  font-size: clamp(14px, 3.6vw, 18px);
  line-height: 1.35;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.72);
`;

function LanguageLoader({ targetLang }: { targetLang: AppLang }) {
  const copy = LANGUAGE_LOADING_COPY[targetLang];
  const dir = targetLang === 'he' ? 'rtl' : 'ltr';

  return (
    <LoaderOverlay $dir={dir} dir={dir} aria-label={copy.label} aria-live="polite" role="status">
      <LoaderCard>
        <LoaderKicker>{copy.label}</LoaderKicker>
        <LoaderTitle>{copy.brand}</LoaderTitle>
        <LoaderSubtitle>{copy.subtitle}</LoaderSubtitle>
        <LoaderBars aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
        </LoaderBars>
      </LoaderCard>
    </LoaderOverlay>
  );
}

function getInitialLang(): AppLang {
  try {
    const firstPathSegment = window.location.pathname.split('/').filter(Boolean)[0];
    if (firstPathSegment === 'en' || firstPathSegment === 'ru' || firstPathSegment === 'fr') {
      return firstPathSegment;
    }
  } catch {}

  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (SUPPORTED_LANGS.includes(stored as AppLang)) {
      return stored as AppLang;
    }
  } catch {}

  return 'he';
}

export const LanguageProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [lang, setLangState] = useState<AppLang>(() => getInitialLang());
  const [i18n, setI18n] = useState<I18nConstants | null>(() => loadedI18n[lang] ?? null);
  const [loadingLang, setLoadingLang] = useState<AppLang | null>(() =>
    loadedI18n[lang] ? null : lang
  );
  const [initialLoaderVisible, setInitialLoaderVisible] = useState(
    isInitialLoaderVisible
  );

  const setLang = useCallback((nextLang: AppLang) => {
    const cached = loadedI18n[nextLang];

    if (cached) {
      setI18n(cached);
      setLoadingLang(null);
    } else {
      setLoadingLang(nextLang);
    }

    setLangState(nextLang);
  }, []);

  const toggleLang = useCallback(() => {
    const nextLang = lang === 'he' ? 'en' : lang === 'en' ? 'ru' : lang === 'ru' ? 'fr' : 'he';
    setLang(nextLang);
  }, [lang, setLang]);
  useEffect(() => {
    let cancelled = false;
    const cached = loadedI18n[lang];

    if (cached) {
      setI18n(cached);
      setLoadingLang(null);
      return () => {
        cancelled = true;
      };
    }

    createI18nAsync(lang).then((nextI18n) => {
      loadedI18n[lang] = nextI18n;
      if (!cancelled) {
        setI18n(nextI18n);
        setLoadingLang(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [lang]);

  useEffect(() => {
    if (!loadingLang) {
      setInitialLoaderVisible(false);
      return;
    }

    const updateInitialLoaderVisibility = () => {
      setInitialLoaderVisible(isInitialLoaderVisible());
    };

    updateInitialLoaderVisibility();

    const observer =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(updateInitialLoaderVisibility)
        : null;

    observer?.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
    });

    const interval = window.setInterval(updateInitialLoaderVisibility, 250);

    return () => {
      observer?.disconnect();
      window.clearInterval(interval);
    };
  }, [loadingLang]);

  // ⭐️⭐️⭐️ שינוי כיוון האתר כולו
  useEffect(() => {
    const isRTL = lang === 'he';

    const html = document.documentElement;

    html.lang = lang;
    html.dir = isRTL ? 'rtl' : 'ltr';

    // optional — class helpers
    html.classList.toggle('rtl', isRTL);
    html.classList.toggle('ltr', !isRTL);

    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {}

  }, [lang]);

  const value = useMemo(
    () => (i18n ? { lang, setLang, toggleLang, i18n } : null),
    [lang, setLang, toggleLang, i18n]
  );

  return (
    <>
      {value && (
        <I18nContext.Provider value={value}>
          {children}
        </I18nContext.Provider>
      )}
      {loadingLang && !loadedI18n[loadingLang] && !initialLoaderVisible && (
        <LanguageLoader targetLang={loadingLang} />
      )}
    </>
  );
};

export const useI18n = (): ContextType => {
  const ctx = useContext(I18nContext);

  if (!ctx) {
    throw new Error(
      'useI18n must be used inside LanguageProvider'
    );
  }

  return ctx;
};
